import { MathUtils, Vector2 } from 'three';
import {
  Areas,
  LogicalSpace,
  Obstacle,
  Point,
  Polygon,
  Rectangle,
  Segment,
} from '@thive/fddm-types';

import {
  computeItemsBoundingBoxExtent,
  computeRectanglesCenterBoundingBox,
} from '@/modules/common/helpers/boundingBox';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { pointsToPolygon } from '@/modules/floorplanDesign/helpers/helpers';
import { AlignMode } from '@/modules/workspace/types/align';
import { assertNever } from '@/helpers/utils';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { AtLeast1, minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import {
  isManualArea,
  isRackingArea,
  isSingularStationArea,
  isStationaryArea,
} from '@/modules/floorplanDesign/helpers/typeguards';
import { Area, Template } from '@/modules/floorplanDesign/types';

import { DimensionField, DimensionKind, EditMode, Geometry } from './types';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';

/** @param itemIds the selected ids without the areas of selected templates */
export const getSelectionKind = (
  itemIds: string[],
  selectedAreas: Area[],
  selectedObstacles: Obstacle[],
  selectedLogicalSpaces: LogicalSpace[],
  selectedTemplates: Template[],
): DimensionKind | null => {
  const total =
    selectedAreas.length +
    selectedObstacles.length +
    selectedLogicalSpaces.length +
    selectedTemplates.length;
  if (total === 0) return null;
  if (total !== itemIds.length) return 'unsupported';
  if (selectedTemplates.length > 0) return 'templates';
  // Walls and angled paths (angled highways, obstacles with more than one segment) can't share
  // sizes or angles with other items, nor can a logical space that isn't a rectangle (rectangular
  // ones are edited like rectangle areas)
  const pathCount =
    selectedAreas.filter((area) => isHighwayArea(area) && area.segments.length > 1).length +
    selectedObstacles.filter(
      ({ appearance, segments }) => appearance === 'WALL' || segments.length > 1,
    ).length;
  const hasPathsWithOthers = pathCount > 0 && pathCount !== total;
  const hasNonRectangularSpace = selectedLogicalSpaces.some(
    (logicalSpace) => !RectangleUtils.polygonToRectangle(logicalSpace.polygon),
  );
  if (hasPathsWithOthers || hasNonRectangularSpace) return 'restricted';
  return 'items';
};

/** @description An angled path: a highway or obstacle (e.g. a wall) with more than one segment */
export const hasAngledPath = (selectedAreas: Area[], selectedObstacles: Obstacle[]): boolean =>
  selectedAreas.some((area) => isHighwayArea(area) && area.segments.length > 1) ||
  selectedObstacles.some(({ segments }) => segments.length > 1);

/**
 * @description Angled paths (highways, walls, obstacles with more than one segment) have no single
 * position, size or angle to edit per item, so a selection with one of them is edited as a group,
 * whatever the other items are. So is a restricted selection (walls, angled paths or
 * non-rectangular logical spaces with other items). Everything else is edited per item, also when
 * the item types differ.
 */
export const getEditMode = (
  kind: DimensionKind | null,
  selectedAreas: Area[],
  selectedObstacles: Obstacle[],
  hasSelectedGroups: boolean,
): EditMode => {
  // A selected group (with or without other items) moves and rotates as one block
  if (kind === 'restricted' || kind === 'templates' || hasSelectedGroups) return 'group';

  return hasAngledPath(selectedAreas, selectedObstacles) ? 'group' : 'individual';
};

export const getAreaRectangles = (area: Area): Rectangle[] => {
  if (isHighwayArea(area)) return area.segments.map(RectangleUtils.fromSegment);
  return [area.rectangle];
};

const groupAreasByType = (areas: Area[]): Areas => ({
  highwayAreas: areas.filter(isHighwayArea),
  rackingAreas: areas.filter(isRackingArea),
  singularStationAreas: areas.filter(isSingularStationArea),
  stationaryAreas: areas.filter(isStationaryArea),
  manualAreas: areas.filter(isManualArea),
});
export const getItemsCenter = (
  areas: Areas,
  obstacles: Obstacle[],
  logicalSpaces: LogicalSpace[],
): { centerX: number; centerY: number } | null => {
  const { x, y, width, height } = computeItemsBoundingBoxExtent(areas, obstacles, logicalSpaces);
  if (![x, y, width, height].every(Number.isFinite)) return null;
  return { centerX: x + width / 2, centerY: y + height / 2 };
};

export const getCenter = (rectangles: Rectangle[]) => {
  if (rectangles.length === 1) {
    const [{ centerX, centerY }] = rectangles;
    return { centerX, centerY };
  }
  const { centerX, centerY } = computeRectanglesCenterBoundingBox(rectangles);
  return { centerX, centerY };
};

export const getPositionDelta = (
  field: DimensionField,
  value: number,
  center: { centerX: number; centerY: number },
): number => {
  if (field === 'centerX') return value - center.centerX;
  if (field === 'centerY') return value - center.centerY;
  return 0;
};

export const normalizeAngle = (angle: number): number =>
  MathUtils.euclideanModulo(angle + Math.PI, Math.PI * 2) - Math.PI;

export const getGroupAngleDelta = (angle: number, rectangles: Rectangle[]): number => {
  const [first] = rectangles;
  if (!first) return 0;
  return normalizeAngle(angle - first.angle);
};

export const rotateRectangleAround = (
  rectangle: Rectangle,
  center: { centerX: number; centerY: number },
  angle: number,
): Rectangle => {
  const rotated = new Vector2(rectangle.centerX, rectangle.centerY).rotateAround(
    new Vector2(center.centerX, center.centerY),
    angle,
  );
  return {
    ...rectangle,
    centerX: rotated.x,
    centerY: rotated.y,
    angle: normalizeAngle(rectangle.angle + angle),
  };
};

/**
 * @description Bounding box size of a group: length along X, width along Y.
 * Read-only, a group (or a bent path) has no single length or width.
 */
export const getBoundingBoxSize = (rectangles: Rectangle[]): { length: number; width: number } => {
  const { width, height } = computeRectanglesCenterBoundingBox(rectangles);
  return { length: width, width: height };
};

/**
 * @description Per item: the items' common angle. As a group: a single item (e.g. one angled
 * highway) shows its first segment's angle; several items only show an angle when every segment
 * shares it, so an angled path among other items shows 'Mixed' instead of its own angle.
 */
const getDisplayAngle = (itemRectangles: Rectangle[][], editMode: EditMode): number | null => {
  if (editMode === 'individual') {
    return getCommonNumber(itemRectangles.flatMap(([first]) => (first ? [first.angle] : [])));
  }
  const [firstItem, ...otherItems] = itemRectangles;
  if (otherItems.length === 0) return firstItem?.[0]?.angle ?? null;
  return getCommonNumber(itemRectangles.flat().map(({ angle }) => angle));
};

export const getGeometry = (
  kind: DimensionKind | null,
  selectedAreas: Area[],
  selectedObstacles: Obstacle[],
  selectedLogicalSpaces: LogicalSpace[],
  editMode: EditMode,
): Geometry | null => {
  // Templates: see getTemplatesGeometry
  if (!kind || kind === 'unsupported' || kind === 'templates') return null;
  if (kind === 'restricted') {
    const { x, y, width, height } = computeItemsBoundingBoxExtent(
      groupAreasByType(selectedAreas),
      selectedObstacles,
      selectedLogicalSpaces,
    );
    if (![x, y, width, height].every(Number.isFinite)) return null;
    // Group size, same as for a group of one type: length along X, width along Y
    return {
      centerX: x + width / 2,
      centerY: y + height / 2,
      length: width,
      width: height,
      angle: null,
      canResize: false,
      canRotate: false,
    };
  }
  const itemRectangles = [
    ...selectedAreas.map(getAreaRectangles),
    ...selectedObstacles.map(({ segments }) => segments.map(RectangleUtils.fromSegment)),
    ...selectedLogicalSpaces.map((logicalSpace) => {
      const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
      return rectangle ? [rectangle] : [];
    }),
  ];
  if (itemRectangles.length === 0 || itemRectangles.some((items) => items.length === 0))
    return null;
  const rectangles = itemRectangles.flat();
  // Multi-segment paths (angled highways, walls) have no single length/width
  const canResize = rectangles.length === itemRectangles.length;
  // ...nor an angle of their own (only their first segment's): with a wall or angled path in the
  // selection the angle is read-only, rotate 90° and flip still turn it as a whole
  const canRotate =
    !selectedObstacles.some(({ appearance }) => appearance === 'WALL') &&
    !hasAngledPath(selectedAreas, selectedObstacles);
  // A group shows its bounding box size (read-only), so never a mixed value
  const groupSize = editMode === 'group' ? getBoundingBoxSize(rectangles) : null;
  const itemCenters = itemRectangles.map(getCenter);
  const selectionCenter = getCenter(rectangles);
  const centerX =
    editMode === 'group'
      ? selectionCenter.centerX
      : getCommonNumber(itemCenters.map((center) => center.centerX));
  const centerY =
    editMode === 'group'
      ? selectionCenter.centerY
      : getCommonNumber(itemCenters.map((center) => center.centerY));
  return {
    centerX,
    centerY,
    length: groupSize ? groupSize.length : getCommonNumber(rectangles.map(({ length }) => length)),
    width: groupSize ? groupSize.width : getCommonNumber(rectangles.map(({ width }) => width)),
    angle: getDisplayAngle(itemRectangles, editMode),
    canResize,
    canRotate,
  };
};

/**
 * @description Offsets that line every item's bounding box up with the selection's bounding box:
 * its left, horizontal centre, right, top, vertical centre or bottom (world axes).
 * Items are only moved, they keep their own rotation. Same order as the items.
 */
export const getAlignOffsets = (itemRectangles: Rectangle[][], mode: AlignMode): Vector2[] => {
  const boxes = itemRectangles.map((rectangles) => {
    const { centerX, centerY, width, height } = computeRectanglesCenterBoundingBox(rectangles);
    return {
      centerX,
      centerY,
      minX: centerX - width / 2,
      maxX: centerX + width / 2,
      minY: centerY - height / 2,
      maxY: centerY + height / 2,
    };
  });
  const selection = computeRectanglesCenterBoundingBox(itemRectangles.flat());

  return boxes.map(({ centerX, centerY, minX, maxX, minY, maxY }) => {
    switch (mode) {
      case AlignMode.LEFT:
        return new Vector2(selection.centerX - selection.width / 2 - minX, 0);
      case AlignMode.CENTER:
        return new Vector2(selection.centerX - centerX, 0);
      case AlignMode.RIGHT:
        return new Vector2(selection.centerX + selection.width / 2 - maxX, 0);
      case AlignMode.TOP:
        return new Vector2(0, selection.centerY + selection.height / 2 - maxY);
      case AlignMode.MIDDLE:
        return new Vector2(0, selection.centerY - centerY);
      case AlignMode.BOTTOM:
        return new Vector2(0, selection.centerY - selection.height / 2 - minY);
      default:
        return assertNever(mode);
    }
  });
};

export type ArrangeTransform = 'rotate90' | 'flipHorizontal' | 'flipVertical';

export type PoseTransform = {
  point: (point: Point) => Point;
  angle: (angle: number) => number;
  /** Mirroring swaps left and right, and flips the winding of polygons */
  mirrors: boolean;
};

/**
 * @description Rotating 90° clockwise or mirroring around the selection centre, like in Figma.
 * Mirroring always goes along the canvas axes, whatever the items' angles, so a flip does the same
 * for every selection: flip vertical mirrors across the vertical axis (swaps left and right), flip
 * horizontal across the horizontal axis (swaps top and bottom). Items move and turn with it.
 * A rectangle keeps its content (e.g. its stations) on the mirrored side; only the order along its
 * length can't be mirrored.
 */
export const getPoseTransform = (
  transform: ArrangeTransform,
  { centerX, centerY }: { centerX: number; centerY: number },
): PoseTransform => {
  switch (transform) {
    case 'rotate90': {
      const pivot = new Vector2(centerX, centerY);
      return {
        point: ({ x, y }) => {
          const rotated = new Vector2(x, y).rotateAround(pivot, -Math.PI / 2);
          return { x: rotated.x, y: rotated.y };
        },
        angle: (angle) => normalizeAngle(angle - Math.PI / 2),
        mirrors: false,
      };
    }
    case 'flipHorizontal':
    case 'flipVertical': {
      // Mirror line through the centre: the vertical axis, or the horizontal axis
      const mirrorAngle = transform === 'flipVertical' ? Math.PI / 2 : 0;
      const cos = Math.cos(2 * mirrorAngle);
      const sin = Math.sin(2 * mirrorAngle);
      return {
        point: ({ x, y }) => {
          const dx = x - centerX;
          const dy = y - centerY;
          return { x: centerX + dx * cos + dy * sin, y: centerY + dx * sin - dy * cos };
        },
        // Mirrored, the width side (where the content is) ends up at 2 * mirrorAngle - itemAngle + 90°,
        // the rectangle whose width points there has this angle (its length is reversed)
        angle: (itemAngle) => normalizeAngle(2 * mirrorAngle - itemAngle + Math.PI),
        mirrors: true,
      };
    }
    default:
      return assertNever(transform);
  }
};

const transformRectangle = (rectangle: Rectangle, { point, angle }: PoseTransform): Rectangle => {
  const { x, y } = point({ x: rectangle.centerX, y: rectangle.centerY });
  return { ...rectangle, centerX: x, centerY: y, angle: angle(rectangle.angle) };
};

export const transformSegments = <T extends Segment>(
  segments: AtLeast1<T>,
  { point, mirrors }: PoseTransform,
): AtLeast1<T> =>
  minItemsArrayUtils.mapAtLeast1(segments, (segment) => ({
    ...segment,
    start: { ...segment.start, ...point(segment.start) },
    end: { ...segment.end, ...point(segment.end) },
    // Mirrored, the left side of a path ends up on its right
    leftSideWidth: mirrors ? segment.rightSideWidth : segment.leftSideWidth,
    rightSideWidth: mirrors ? segment.leftSideWidth : segment.rightSideWidth,
  }));

export const transformArea = (area: Area, transform: PoseTransform): Area => {
  if (isHighwayArea(area))
    return { ...area, segments: transformSegments(area.segments, transform) };
  return { ...area, rectangle: transformRectangle(area.rectangle, transform) };
};

export const transformPolygon = (polygon: Polygon, { point, mirrors }: PoseTransform): Polygon => {
  const points = polygon.points.map((polygonPoint) => ({
    ...polygonPoint,
    ...point(polygonPoint),
  }));
  // Mirroring flips the winding, reverse to keep it
  return pointsToPolygon(mirrors ? points.reverse() : points) ?? polygon;
};
